import { createContext, createElement, lazy, Suspense, useContext } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

// A component one module lends to others, by a typed token. The token lives in
// the owner's -client package; the owner's declaration lends the loader with
// withLends; a consumer renders <Lent token={…} props={…} />. Props are the
// contract; neither side imports the other's browser package.

export type LentToken<TProps> = {
  readonly kind: "lent-component";
  readonly id: string;
  /** Phantom: carries the props type. Never set. */
  readonly props?: TProps;
};

export function lentComponent<TProps>(owner: string, name: string): LentToken<TProps> {
  return Object.freeze({ kind: "lent-component", id: `${owner}.${name}` });
}

/** Loaders by token id; the component type is erased here and restored by the token. */
export type LentRegistry = ReadonlyMap<string, () => Promise<{ readonly default: unknown }>>;

export const LentContext = createContext<LentRegistry>(new Map());

const built = new Map<string, LazyExoticComponent<ComponentType<object>>>();

function lentView<TProps>(token: LentToken<TProps>, registry: LentRegistry): ComponentType<TProps> {
  const cached = built.get(token.id);
  // The owner lent this loader under the token's id, with the token's props type.
  if (cached) return cached as unknown as ComponentType<TProps>;
  const load = registry.get(token.id);
  if (!load) throw new Error(`No installed module lends "${token.id}".`);
  const view = lazy(async () => ({ default: (await load()).default as ComponentType<TProps> }));
  built.set(token.id, view as unknown as LazyExoticComponent<ComponentType<object>>);
  return view as unknown as ComponentType<TProps>;
}

export function Lent<TProps extends object>({
  token,
  props,
}: {
  readonly token: LentToken<TProps>;
  readonly props: TProps;
}) {
  const view = lentView(token, useContext(LentContext));
  return <Suspense fallback={null}>{createElement(view, props)}</Suspense>;
}
